import React, { useRef, useEffect } from 'react';
import { Github, Mail } from 'lucide-react';
import { OAuthProvider } from '../../types';
import { authAPI } from '../../services/api';

interface OAuthButtonsProps {
  onError: (error: string) => void;
}

export const OAuthButtons: React.FC<OAuthButtonsProps> = ({ onError }) => {
  const currentProvider = useRef<OAuthProvider | null>(null);

  useEffect(() => {
    const handleOAuthMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data.type === 'oauth-callback' && event.data.provider === currentProvider.current) {
        try {
          await authAPI.loginWithOAuth(event.data.provider, event.data.code);
          // Redirect to the stored URL or dashboard
          const returnTo = localStorage.getItem('oauth_return_to') || '/dashboard';
          localStorage.removeItem('oauth_return_to'); // Clean up
          window.location.href = returnTo;
        } catch (error: any) {
          onError(error.message);
        }
      }
    };
    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [onError]);

  const handleOAuthLogin = async (provider: OAuthProvider) => {
    try {
      // Store the current URL or default to dashboard
      const returnTo = window.location.pathname === '/login' ? '/dashboard' : window.location.pathname;
      localStorage.setItem('oauth_return_to', returnTo);
      currentProvider.current = provider;
      // Get the OAuth URL from the API
      const oauthUrl = authAPI.getOAuthUrl(provider);
      // Open the OAuth popup
      const width = 600;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        oauthUrl,
        `${provider}Login`,
        `width=${width},height=${height},left=${left},top=${top}`
      );
      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }
    } catch (error: any) {
      onError(error.message);
    }
  };

  return (
    <div className="flex flex-col space-y-3">
      <button
        onClick={() => handleOAuthLogin('google')}
        className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <Mail className="w-5 h-5 mr-2" />
        Continue with Google
      </button>
      <button
        onClick={() => handleOAuthLogin('github')}
        className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-gray-800 border border-transparent rounded-md shadow-sm hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
      >
        <Github className="w-5 h-5 mr-2" />
        Continue with GitHub
      </button>
    </div>
  );
}; 